import type { FC, ReactNode } from "react";
import React, { useMemo } from "react";
import { useAppConfig } from "@edreamsodigeo/frontend-app-config";

import AppProvider from "../app/AppProvider";
import type { Brands } from "../brand/types";
import {
  AppErrorProvider,
  BlockerErrorBoundary,
  ErrorNamespaceProvider,
} from "../error_handling";
import type { CurrencyFormat } from "../types";
import { getMicroFrontendProps } from "./MFEProvider.props";
import { MFERouterProvider } from "./MFERouterProvider";

const noop = () => {};

const legacyErrorHandler = {
  dispatchRenderError: noop,
  dispatchFrontendAPIError: noop,
  restore: noop,
  onRestore: noop,
  stopOnRestore: noop,
};

const legacyLogger = {
  error: noop,
  info: noop,
  message: noop,
  warn: noop,
  disableLoggers: noop,
};

const legacyFlowActions = { goToHome: noop, openFlightSummary: noop };
export interface MicroFrontEndConfig {
  name: string;
  version: string;
  logEndpoint?: string;
}
interface DateFnsLocale {
  code: string;
}
interface PrismaProviderProps {
  brand: Brands;
  version: "bluestone" | "cobalt";
  currencyLocale: {
    locale: string;
    minimumFractionDigits?: number;
    numberLocale?: string;
  };
  currencyFormat?: CurrencyFormat;
  dateFnsLocale: DateFnsLocale;
  children: ReactNode;
}
interface MFEPageProviderProps {
  children: ReactNode;
  mfe: MicroFrontEndConfig;
  onRestartAppStatus: () => void;
  fallback: ReactNode;
  PrismaProvider?: FC<PrismaProviderProps>;
}

const DefaultPrismaProvider: FC<{ children: ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

const MFEPageProvider = ({
  children,
  mfe,
  onRestartAppStatus,
  fallback,
  PrismaProvider = DefaultPrismaProvider,
}: MFEPageProviderProps) => {
  const appConfig = useAppConfig();
  const microFrontEndProps = useMemo(
    () => getMicroFrontendProps(appConfig, mfe),
    [appConfig, mfe],
  );

  const errorBoundaryName = `${mfe?.name}_blocking_error`;

  return (
    <ErrorNamespaceProvider namespace={mfe?.name}>
      <AppProvider
        {...microFrontEndProps}
        errorHandler={legacyErrorHandler}
        logger={legacyLogger}
        flowActions={legacyFlowActions}
      >
        <AppErrorProvider
          logService={mfe?.logEndpoint}
          onRestartAppStatus={onRestartAppStatus}
          frontVersion={mfe?.version}
        >
          <BlockerErrorBoundary name={errorBoundaryName}>
            <PrismaProvider
              brand={appConfig.brand}
              version="cobalt"
              currencyLocale={appConfig.locale.currency}
              dateFnsLocale={appConfig.locale.dates as DateFnsLocale}
              currencyFormat={appConfig.locale.currencyFormat}
            >
              <MFERouterProvider>{children}</MFERouterProvider>
            </PrismaProvider>
          </BlockerErrorBoundary>
          {fallback}
        </AppErrorProvider>
      </AppProvider>
    </ErrorNamespaceProvider>
  );
};

export default MFEPageProvider;
